import React, { useState, useMemo } from 'react';
import styled from 'styled-components';
import NoAllocations from './NoAllocations';
import DonationDetailsVisual from './DonationDetailsVisual';
import DonationDetailsColumn from './DonationDetailsColumn';
import TrueImpactButton from 'shared/design-system/components/TrueImpactButton';
import { DonationsAllocation, Investment } from '../api';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { DonationVisualProps, generateColorShades } from './utils';
import TrueImpactSimpleModal from 'shared/design-system/components/TrueImpactSimpleModal';
import {
  amountsApproximatelyEqual,
  numberWithCommas,
} from 'shared/utils/numberUtils';
import AllocateReportModal from 'shared/components/WelcomeBack/AllocateReportModal';
import { Task } from '../../Tasks/api';
import { useGraphQLRequest } from 'shared/auth/hooks/useApiRequest';
import { deleteAllocation } from './api';
import { useClient } from 'shared/apollo';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBrakeWarning } from '@fortawesome/pro-light-svg-icons';
import { requestInit } from 'shared/auth/hooks/useRequestState';

const colorList = [COLORS.ctaPrimary, COLORS.grayBase];
export const totalAllocatedAmount = (
  allocations: Investment['allocations'],
  total: number,
) => {
  const colors =
    allocations && allocations?.length > 1
      ? generateColorShades(colorList, allocations?.length)
      : [COLORS.ctaPrimary];
  let calculatedTotalDonations = 0;
  const list =
    allocations?.map((a, i) => {
      calculatedTotalDonations += a.amount;
      const result: DonationVisualProps = {
        color: colors[i],
        amount: a.amount,
        year: a.report?.year,
        status: a.report?.status,
        reportId: a.report?.dbId,
        programName: a.report?.program?.name,
      };
      return result;
    }) || [];
  return {
    list,
    calculatedTotalDonations,
    unallocatedAmount: amountsApproximatelyEqual(
      total,
      calculatedTotalDonations,
    )
      ? 0
      : total - calculatedTotalDonations,
  };
};
export const totalAllocatedDonations = (donation: Investment) => {
  const total = donation.donation;
  return totalAllocatedAmount(donation.allocations, total);
};

interface DonationWhereContentProps {
  donation: Investment;
}

const StyledDiv = styled.div`
  padding: ${SPACERS.medium};
  .content-wrapper.content-wrapper-center {
    text-align: center;
    justify-content: center;
    padding-top: 48px;
    padding-bottom: 48px;
  }
  .content-wrapper {
    display: flex;
    padding: ${SPACERS.large};
    gap: ${SPACERS.extraLarge};
    border-radius: ${SPACERS.extraTiny};
    border: 1px solid ${COLORS.grayLight};
    background-color: ${COLORS.whiteBase};
    .visual-column {
      width: 315px;
      .change-donation-button {
        width: 100%;
        margin-top: ${SPACERS.large};
      }
      .unallocated-text {
        margin-top: ${SPACERS.small};
      }
    }
  }
  .confirmation-wrapper {
    max-width: 400px;
    .icon-container {
      text-align: center;
      margin-bottom: ${SPACERS.small};
    }
    .error-text {
      margin-top: ${SPACERS.small};
    }
  }
`;

const DonationWhereContent: React.FC<DonationWhereContentProps> = ({
  donation,
}) => {
  const client = useClient();
  const [saveRequest, makeRequest, setRequest] = useGraphQLRequest();
  const [showAllocationModal, setShowAllocationModal] = useState<
    Task | undefined
  >(undefined);
  const [allocations, setAllocations] = useState<
    NonNullable<Investment['allocations']>
  >(donation.allocations || []);
  const [showConfirmation, setShowConfirmation] = useState<
    { index: number; allocation: DonationsAllocation } | undefined
  >(undefined);

  const { list, calculatedTotalDonations, unallocatedAmount: localRemainder } =
    useMemo(
      () => totalAllocatedAmount(allocations, donation.donation),
      [allocations, donation.donation],
    );
  // The server owns the remainder; the local figure only covers allocations
  // detached in this session, before the donation is refetched.
  const unallocatedAmount =
    donation.unallocatedAmount !== undefined &&
    allocations.length === (donation.allocations?.length || 0)
      ? donation.unallocatedAmount
      : localRemainder;
  const [selectedIndex, setSelectedIndex] = useState<number | undefined>(
    undefined,
  );

  const task = useMemo(() => {
    const result = donation.allocations?.find(
      allocation => allocation.tasks?.length > 0,
    );
    return result?.tasks[0];
  }, [donation.allocations]);

  const handleDetach = (index: number, allocation: DonationsAllocation) => {
    if (!client) return;
    const request = deleteAllocation(client, allocation.dbId);
    makeRequest(request);
    request.then(r => {
      if (r.data?.deleteAllocation?.success) {
        setAllocations(prev => prev.filter((_, i) => i !== index));
        setShowConfirmation(undefined);
        setRequest(requestInit());
      }
    });
  };

  const handleAllocateUnallocated = () => {
    // The wizard reads the investment and its remainder from the task itself,
    // so nothing is fabricated or overridden here.
    if (unallocatedAmount <= 0 || !task) return;
    setShowAllocationModal(task);
  };

  if (!donation.allocations || donation.allocations.length === 0) {
    return (
      <StyledDiv>
        <div className="content-wrapper content-wrapper-center">
          <NoAllocations donation={donation} />
        </div>
      </StyledDiv>
    );
  }

  return (
    <>
      <StyledDiv>
        <div className="content-wrapper">
          <div className="visual-column">
            <DonationDetailsVisual
              donationList={list}
              donation={donation}
              selectedIndex={selectedIndex}
              unallocatedAmount={unallocatedAmount}
              calculatedTotalDonations={calculatedTotalDonations}
              setSelectedIndex={setSelectedIndex}
            />
            {unallocatedAmount > 0 && (
              <TrueImpactButton
                variant="secondary-large"
                className="change-donation-button"
                onClick={handleAllocateUnallocated}
                disabled={unallocatedAmount <= 0 || !task}
              >
                Allocate ${numberWithCommas(unallocatedAmount.toFixed(2))}
              </TrueImpactButton>
            )}
          </div>
          <DonationDetailsColumn
            donationList={list}
            donation={donation}
            allocations={allocations}
            selectedIndex={selectedIndex}
            unallocatedAmount={unallocatedAmount}
            calculatedTotalDonations={calculatedTotalDonations}
            setSelectedIndex={setSelectedIndex}
            handleDetach={(i, a) =>
              setShowConfirmation({ index: i, allocation: a })
            }
          />
        </div>
      </StyledDiv>
      <TrueImpactSimpleModal
        open={Boolean(showConfirmation)}
        onClose={() => setShowConfirmation(undefined)}
        previous={{
          text: 'Cancel',
          onClick: () => setShowConfirmation(undefined),
        }}
        next={{
          text: 'Detach',
          backgroundColor: 'critical',
          backgroundColorHover: 'criticalHover',
          isLoading: saveRequest.type === 'REQUEST_START',
          onClick: () =>
            showConfirmation &&
            handleDetach(showConfirmation.index, showConfirmation.allocation),
        }}
      >
        <StyledDiv>
          <div className="confirmation-wrapper">
            <div className="icon-container">
              <FontAwesomeIcon
                size="3x"
                className="icon"
                icon={faBrakeWarning}
                color={COLORS.ctaPrimary}
              />
            </div>
            <TrueImpactText>
              Are you sure? This will remove the donation allocation from this
              report. You will then have to re-allocate the donation to another
              report (or reports).
            </TrueImpactText>
            {saveRequest.type === 'REQUEST_ERROR' && (
              <TrueImpactText color="critical" className="error-text">
                Unable to detach the allocation. Please try again!
              </TrueImpactText>
            )}
          </div>
        </StyledDiv>
      </TrueImpactSimpleModal>
      {showAllocationModal && (
        <AllocateReportModal
          report={undefined}
          task={showAllocationModal}
          onClose={() => setShowAllocationModal(undefined)}
        />
      )}
    </>
  );
};

export default DonationWhereContent;
